import React, { useState } from 'react';
import { Checkbox, notification, Radio, Space, Tag } from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import { useTranslation } from 'react-i18next';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import { AddressItem, UpdateProfileDTO } from 'types/dto/order';
import './AddressEdit.scss';
import { getCssVar } from 'utils/commonUtils';
import { fetchUpdateAddress } from 'services/profileService';
// import { useSession } from 'context/SessionContext';
import { MemberProfile } from 'types/dto/profile';
import { useOutletContext } from 'react-router-dom';

export type AddressEditMode = 'delete' | 'select-default';

interface AddressEditProps {
  visible: boolean;
  mode: AddressEditMode;
  addresses: AddressItem[];
  onClose: () => void;
  onSuccess?: () => void;
}

const AddressEdit: React.FC<AddressEditProps> = ({
  visible,
  mode,
  addresses,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const { session } = useSession();
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  // Local state for selections
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>([]);
  const [localDefaultId, setLocalDefaultId] = useState<number>(0);

  const isSaveEnabled =
    mode === 'delete' ? localSelectedIds.length > 0 : localDefaultId !== 0;

  const handleCheckboxChange = (addressId: number, checked: boolean) => {
    if (checked) {
      setLocalSelectedIds((prev) => [...prev, addressId]);
    } else {
      setLocalSelectedIds((prev) => prev.filter((id) => id !== addressId));
    }
  };

  const handleRadioChange = (e: RadioChangeEvent) => {
    setLocalDefaultId(Number(e.target.value));
  };

  const handleCancel = () => {
    // Reset local state to initial values
    setLocalSelectedIds([]);
    setLocalDefaultId(0);
    onClose();
  };

  //there is no default property provided by backend, so we need to set the first one(seq == 1) as default
  const handleSelectDefault = async (newDefaultId: number) => {
    try {
      setIsSubmitting(true);
      const newDefaultAddr = addresses.find(
        (addr) => addr.addressID === newDefaultId
      );

      if (!newDefaultAddr) {
        notification.error({
          message: tCommon('error.updateDefaultAddressFailed'),
          description: 'Please select default address',
          duration: 1.5,
        });
        return;
      }

      const otherAddrs = addresses.filter(
        (addr) => addr.addressID !== newDefaultId
      );

      const updatedAddresses: AddressItem[] = [
        {
          ...newDefaultAddr,
          seq: 1,
        },
        ...otherAddrs.map((addr, index) => ({
          ...addr,
          seq: index + 2,
        })),
      ];

      const dto: UpdateProfileDTO = {
        juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
        addresses: updatedAddresses,
      };

      await fetchUpdateAddress('/profile', dto);
      // console.log('Default address updated successfully:', newDefaultId);

      setLocalSelectedIds([]);
      setLocalDefaultId(0);

      onSuccess?.();
    } catch (error) {
      notification.error({
        message: tCommon('error.updateAddressFailed'),
        description: (error as Error).message,
        duration: 1.5,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddressDelete = async (selectedDeleteIds: number[]) => {
    try {
      setIsSubmitting(true);
      let keepAddresses = addresses.filter(
        (addr) => !selectedDeleteIds.includes(addr.addressID)
      );

      keepAddresses = keepAddresses.map((addr, index) => {
        //seq start from 1
        const newSeq = index + 1;
        return {
          ...addr,
          seq: newSeq,
        };
      });

      const dto: UpdateProfileDTO = {
        juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
        addresses: keepAddresses,
      };

      await fetchUpdateAddress('/profile', dto);

      setLocalSelectedIds([]);
      setLocalDefaultId(0);

      onSuccess?.();
    } catch (error) {
      notification.error({
        message: tCommon('error.deleteAddressFailed'),
        description: (error as Error).message,
        duration: 1.5,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (mode === 'delete') {
      handleAddressDelete?.(localSelectedIds);
    } else {
      handleSelectDefault?.(localDefaultId);
    }
  };

  const isDeleteMode = mode === 'delete';
  const title = isDeleteMode
    ? t('addressEdit.removeAddress')
    : t('addressEdit.selectDefaultAddress');
  const description = isDeleteMode
    ? t('addressEdit.description')
    : t('addressEdit.selectDescription');
  const primaryButtonText = isDeleteMode
    ? tCommon('button.remove')
    : tCommon('button.confirm');

  return (
    <DrawerFormContainer
      title={title}
      description={description}
      visible={visible}
      onClose={handleCancel}
      onSave={handleSave}
      saveButtonTitle={primaryButtonText}
      isSubmitting={isSubmitting}
      isSaveButtonDisabled={!isSaveEnabled}
    >
      <div className="address-edit-content">
        {/* Address List */}
        <div className="address-list">
          {isDeleteMode ? (
            // Delete mode - Checkboxes
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {addresses.map((address) => (
                <div
                  key={address.addressID}
                  className={`address-item ${localSelectedIds.includes(address.addressID)
                      ? 'selected'
                      : ''
                    }`}
                >
                  <Checkbox
                    checked={localSelectedIds.includes(address.addressID)}
                    onChange={(e) =>
                      handleCheckboxChange(address.addressID, e.target.checked)
                    }
                    className="address-checkbox"
                  >
                    <div className="address-content">
                      <div>{address.address}</div>
                      {address.seq === 1 && (
                        <Tag className="address-tag">
                          {t('addressEdit.default')}
                        </Tag>
                      )}
                    </div>
                  </Checkbox>
                </div>
              ))}
            </Space>
          ) : (
            // Select default mode - Radio buttons
            <Radio.Group
              value={localDefaultId}
              onChange={handleRadioChange}
              className="address-radio-group"
            >
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {addresses.map((address) => (
                  <div
                    key={address.addressID}
                    className={`address-item ${localDefaultId === address.addressID ? 'selected' : ''
                      }`}
                  >
                    <Radio value={address.addressID} className="address-radio">
                      <div className="address-content">
                        <div>{address.address}</div>
                        {address.seq === 1 && (
                          <Tag
                            className="address-tag"
                            color={getCssVar('--vls-grey-light')}
                          >
                            {t('addressEdit.currentDefault')}
                          </Tag>
                        )}
                      </div>
                    </Radio>
                  </div>
                ))}
              </Space>
            </Radio.Group>
          )}
        </div>
      </div>
    </DrawerFormContainer>
  );
};

export default AddressEdit;
